import { ChangeDetectorRef, Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Endpoint, HelpIndex, MessageType, ReviewSystem, UpdateStatusType } from '../../../models/enums';
import { ExportStream } from '../../../models/export-stream';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { ExportComponent } from '../../export/export.component';
import { isUndefined, isNullOrUndefined, isNumber } from '../../../models/utils';
import { DrObject } from '../../../models/dr-object';

@Component({
    standalone: false,
    selector: 'res-export-modal',
    templateUrl: './export-modal.component.html',
    styleUrls: ['./export-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ExportModalComponent extends BaseModalComponent {
    @ViewChild(ExportComponent, { static: true }) exportComponent: ExportComponent;

    modalTitle = 'Export';
    modalAutomationId = 'export-modal';
    isBusy = true;
    isBusyMessage = 'Loading...';
    modalHelpIndex = HelpIndex.exports_ediscexports_dlg;
    modalClass = 'export-modal';
    exportVolumesAllowed: boolean;

    selectedExportStream: ExportStream;

    constructor(
        private modalService: BsModalService,
        private commonService: CommonService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private cdRef: ChangeDetectorRef
    ) {
        super(modalService, modalRef);
        if (this.modalData instanceof ExportStream) {
            this.selectedExportStream = this.modalData;
        } else if (this.modalData?.exportVolumesAllowed) {
            this.exportVolumesAllowed = true;
        }
    }

    runExport = (): void => {
        const runExportOperation = (): void => {
            const exportOptions = this.exportComponent.getExportStreamOptions();
            const extraOptions = this.exportComponent.extraOptions;
            const dataAreaName = extraOptions.exportDataAreas.find(eda => eda.handle === exportOptions.dataAreaHandle)?.name;
            const taskDescription = `Exporting ${extraOptions.exportStreamName} at ${dataAreaName} : ${this.commonService.currentProject.name} - ${extraOptions.exportStreamName}`;
            if (this.exportComponent.isExportVolume || this.exportVolumesAllowed) {
                this.isBusyMessage = 'Exporting...';
                this.isBusy = true;

                if (this.exportVolumesAllowed) {
                    // Updating the values of the Default Export Stream from the commonService component with the user's choice of exportOptions
                    this.commonService.currentProject.tlsDefaultExportStream.options.pdfSubdirectoryName = exportOptions.pdfSubdirectoryName;
                    this.commonService.currentProject.tlsDefaultExportStream.options.convertImagesToPdf = exportOptions.convertImagesToPdf;
                    this.commonService.currentProject.tlsDefaultExportStream.options.highlightTerms = exportOptions.highlightTerms;
                    this.commonService.currentProject.tlsDefaultExportStream.options.includePdfConversion = exportOptions.includePdfConversion;
                    this.commonService.currentProject.tlsDefaultExportStream.options.custodianReportQueries = exportOptions.custodianReportQueries;
                }
                let exportToRelativityTaskDescription: string, exportToRelativityHandle: string;
                let exportApiName = Endpoint.groupViewManager_exportView;
                if (this.exportVolumesAllowed) {
                    // Export to Relativity Modal related code 
                    exportToRelativityTaskDescription = `Exporting ${this.commonService.currentProject.tlsDefaultExportStream?.name} at ${this.commonService.currentProject.tlsDefaultExportStream?.options?.dataAreaName} : ${this.commonService.currentProject.name} - ${this.commonService.currentProject.tlsDefaultExportStream?.name}`;
                    exportToRelativityHandle = this.modalData.entityView?.handle ? this.modalData.entityView.handle : this.modalData.entity?.handle ? this.modalData.entity.handle : '';
                    exportApiName = Endpoint.groupViewManager_exportViewLite;
                }
                const request: any = {
                    exportStreamHandle: this.exportVolumesAllowed ? this.commonService.currentProject?.tlsDefaultExportStream?.handle : this.exportComponent.selectedExportStream.handle,
                    srcViewHandle: this.exportVolumesAllowed ? exportToRelativityHandle : this.commonService.projectData.handle,
                    taskDescription: this.exportVolumesAllowed ? exportToRelativityTaskDescription : taskDescription,
                    exportStreamOptions: this.exportVolumesAllowed ? this.commonService.currentProject.tlsDefaultExportStream.options : exportOptions,
                    metadataTemplateHandle: exportOptions.metadataTemplateHandle,
                };
                this.restService.post(exportApiName, request).then(response => {
                    this.isBusy = false;
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.EXPORT_VIEW);
                }, error => {
                    this.isBusy = false;
                });
                this.closeModal();
            } else {
                this.isBusyMessage = 'Exporting...';
                this.isBusy = true;
                const request: any = {
                    name: extraOptions.exportStreamName,
                    attributes: [{ name: 'exportStreamView', value: this.commonService.projectData.handle }],
                    options: exportOptions,
                    viewHandle: this.commonService.projectData.handle,
                    description: taskDescription,
                    taskDescription: taskDescription,
                    metadataTemplateHandle: exportOptions.metadataTemplateHandle
                };
                this.restService.post(Endpoint.ecaManager_createExportStreamAndExport, request).then(response => {
                    this.isBusy = false;
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.EXPORT_VIEW);
                }, error => {
                    this.isBusy = false;
                });
                this.closeModal(true, { name: extraOptions.exportStreamName, entityType: UpdateStatusType.EXPORT_VIEW });
            }
        }
        const exportOptions = this.exportComponent.getExportStreamOptions();
        const extraOptions = this.exportComponent.extraOptions;
        const dataAreaName = extraOptions.exportDataAreas.find(eda => eda.handle === exportOptions.dataAreaHandle)?.name;

        if (isNullOrUndefined(dataAreaName)) {
            this.exportComponent.exportOptions.dataAreaHandle = null;
        }
        this.checkValidationAndMakeOperation(runExportOperation);
    }

    stageExport = (): void => {
        const stageExportOperation = (): void => {
            const exportOptions = this.exportComponent.getExportStreamOptions();
            const extraOptions = this.exportComponent.extraOptions;
            const dataAreaName = extraOptions.exportDataAreas.find(eda => eda.handle === exportOptions.dataAreaHandle).name;
            const taskDescription = `Staging ${extraOptions.exportStreamName} at ${dataAreaName} : ${this.commonService.currentProject.name} - ${extraOptions.exportStreamName}`;
            if (this.exportComponent.isExportVolume) {
                this.isBusyMessage = 'Staging Export...';
                this.isBusy = true;
                const request: any = {
                    exportStreamHandle: this.exportComponent.selectedExportStream.handle,
                    viewHandle: this.commonService.projectData.handle,
                    taskDescription: taskDescription,
                    exportStreamOptions: exportOptions,
                    metadataTemplateHandle: exportOptions.metadataTemplateHandle
                };
                this.restService.post(Endpoint.groupViewManager_previewExport, request).then(response => {
                    this.isBusy = false;
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.EXPORT_VIEW);
                }, error => {
                    this.isBusy = false;
                });
                this.closeModal();
            } else {
                this.isBusyMessage = 'Staging Export...';
                this.isBusy = true;
                const request: any = {
                    name: extraOptions.exportStreamName,
                    attributes: [{ name: 'exportStreamView', value: this.commonService.projectData.handle }],
                    options: exportOptions,
                    viewHandle: this.commonService.projectData.handle,
                    description: taskDescription,
                    taskDescription: taskDescription,
                    metadataTemplateHandle: exportOptions.metadataTemplateHandle
                };
                this.restService.post(Endpoint.ecaManager_createExportStreamAndPreviewExport, request).then(response => {
                    this.isBusy = false;
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.EXPORT_VIEW);
                }, error => {
                    this.isBusy = false;
                });
                this.closeModal(true, { name: extraOptions.exportStreamName, entityType: UpdateStatusType.EXPORT_VIEW });
            }
        }
        this.checkValidationAndMakeOperation(stageExportOperation);
    }

    saveEmptyExportDisabled = (): boolean => this.exportComponent && this.exportComponent.isExportVolume;

    saveEmptyExport = (): void => {
        const saveEmptyExportOperation = (): void => {
            this.isBusyMessage = 'Saving Empty Export...';
            this.isBusy = true;
            const exportOptions = this.exportComponent.getExportStreamOptions();
            const extraOptions = this.exportComponent.extraOptions;
            const dataAreaName = extraOptions.exportDataAreas.find(eda => eda.handle === exportOptions.dataAreaHandle).name;
            const taskDescription = `Exporting ${extraOptions.exportStreamName} at ${dataAreaName} : ${this.commonService.currentProject.name} - ${extraOptions.exportStreamName}`;

            const request: any = {
                name: extraOptions.exportStreamName,
                attributes: [{ name: 'exportStreamView', value: this.commonService.projectData.handle }],
                options: exportOptions,
                viewHandle: this.commonService.projectData.handle,
                description: taskDescription,
                taskDescription: taskDescription,
                metadataTemplateHandle: exportOptions.metadataTemplateHandle
            };
            this.restService.post(Endpoint.ecaManager_createEmptyExportStream, request).then(response => {
                this.isBusy = false;
                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.EXPORT_VIEW);
            }, error => {
                this.isBusy = false;
            });
            this.closeModal(true, { name: extraOptions.exportStreamName, entityType: UpdateStatusType.EXPORT_VIEW });
        }
        this.checkValidationAndMakeOperation(saveEmptyExportOperation);
    }

    isButtonDisabled = (): boolean => {
        let isDisabled = true;
        if (this.exportComponent.exportStreamNameDescriptionComponent.formValid) {
            if ((this.exportComponent.exportOptions && !isUndefined(this.exportComponent.exportOptions.dataAreaHandle) &&
                (!DrObject.isEmpty(this.exportComponent.exportStreamNameDescriptionComponent.name.trim())) &&
                (this.exportComponent.exportOptions.dataAreaHandle !== null))) {
                if (!DrObject.isEmpty(this.exportComponent.documentsToExport.query, true)) {
                    isDisabled = false;
                }

                if (!isNullOrUndefined(this.exportComponent.extraOptions.selectedExportDatabaseConnection) &&
                    (DrObject.isEmpty(this.exportComponent.tableNameDescriptionComponent.name) || (this.exportComponent.tableNameDescriptionComponent.formValid === false))) {
                    isDisabled = true;
                }
            }
        }
        return isDisabled;
    }

    checkValidationAndMakeOperation = (makeOperation): void => {
        if (this.exportComponent.valid(true)) {
            if (!this.exportComponent.validation.exportStreamName.isNameValidated) {
                this.exportComponent.validateName(this.exportComponent.exportStreamNameDescriptionComponent).then((response) => {
                    if (response?.valid) {
                        makeOperation();
                    }
                });
            }
            else {
                makeOperation();
            }
        }

        if (this.exportVolumesAllowed && !this.exportComponent.validation.directory.pdfSubdirectoryInput.hasError) {
            makeOperation();
        }
    }

    checkForErrors(): boolean {
        if (!this.exportVolumesAllowed) {
            const isExportDAAvailable = this.exportComponent.extraOptions?.exportDataAreas.find(eda => eda?.handle === this.exportComponent.exportOptions?.dataAreaHandle);
            if (this.exportComponent && (this.exportComponent.validation.basePath.hasError || this.exportComponent.validation.volumeLabel.hasError
                || this.exportComponent.validation.exportStreamName.hasError || this.exportComponent.validation.dataAreaHandle.hasError
                || (isNullOrUndefined(this.exportComponent.selectedDataAreaName) && (this.exportComponent.exportOptions.dataAreaHandle && !isExportDAAvailable))
                || this.exportComponent.validation.documentsToExport.hasError || this.exportComponent.validation.nearDuplicatesThreshold.hasError
                || this.exportComponent.validation.nearDuplicatesMinTermCount.hasError || this.exportComponent.validation.tableName.hasError
                || this.exportComponent.validation.maxManifestRecords.hasError
                || (!isNumber(this.exportComponent.exportOptions.maxManifestRecords) && this.exportComponent.extraOptions.maxRecordsPerFile)
                || this.exportComponent.validation.postProcessDataAreaHandle.hasError
                || (!this.exportComponent.exportOptions.postProcessDataAreaHandle && this.exportComponent.exportOptions.postProcess))
                || this.exportComponent.validation.directory.nativeSubdirectoryInput.hasError || this.exportComponent.validation.directory.pdfSubdirectoryInput.hasError
                || this.exportComponent.validation.directory.textSubdirectoryInput.hasError || this.exportComponent.validation.directory.tiffSubdirectoryInput.hasError
                || this.exportComponent.validation.documentIdPrefix.hasError || this.exportComponent.validation.startingDocumentId.invalid
                || this.exportComponent.validation.dateFormat.invalid || this.exportComponent.validation.dateTimeDelimiter.invalid || this.exportComponent.validation.timeFormat.invalid
                || (this.exportComponent.currentReviewSystem !== ReviewSystem.NONE && this.reviewSpecificDataIsInvalid())
                || this.isButtonDisabled()) {
                return true;
            }
        } else if (this.exportVolumesAllowed) {
            if (this.exportComponent && this.exportComponent.validation.directory.pdfSubdirectoryInput.hasError) {
                return true;
            }
        }
    }

    reviewSpecificDataIsInvalid(): boolean {
        if (this.exportComponent.exportOptions.createReviewFolder) {
            return !this.exportComponent.exportOptions.reviewProjectId || this.exportComponent.validation.reviewProjectId.invalid;
        } else {
            return !this.exportComponent.exportOptions.reviewProjectId || !this.exportComponent.exportOptions.reviewFolderId || this.exportComponent.exportOptions.reviewFolderId?.toString() === '-1' || this.exportComponent.validation.documentFolder.invalid || this.exportComponent.validation.reviewProjectId.invalid;
        }
    }

    exportNameNotValidated(): boolean {
        return this.exportComponent.exportNameChangedByTyping && !this.checkForErrors();
    }
}
